import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import API_URL from '../../config/api';
import { showToast } from '../../utils/toast';
import { PROPERTY_PAGE_CSS } from './propertyPageStyles';
import {
  DEFAULT_TENANT_FORM, DEFAULT_REF_FORM, HOUSING_REQ_DEFAULTS, TenantFormState, RefFormState,
  tenantToForm, serializeTenantForm, UNIT_LABELS,
} from './tenantFormShared';

const CSS = `
.tf-page { min-height: 100vh; background: #fafafa; font-family: 'DM Sans', system-ui, sans-serif; padding-bottom: 88px; }
.tf-top { position: sticky; top: 0; z-index: 20; background: #fff; border-bottom: 1px solid #e5e7eb; padding: 16px 24px; }
.tf-back { background: none; border: none; color: #374151; font-size: 13px; font-weight: 600; cursor: pointer; padding: 0; margin-bottom: 8px; display: block; }
.tf-title { margin: 0; font-size: 22px; font-weight: 800; color: #111; }
.tf-sub { margin: 4px 0 0; font-size: 12px; color: #6b7280; }
.tf-body { max-width: 920px; margin: 0 auto; padding: 24px; }
.tf-section { background: #fff; border: 1px solid #e5e7eb; border-radius: 10px; padding: 20px 22px; margin-bottom: 16px; }
.tf-section-title { font-size: 11px; font-weight: 800; letter-spacing: 0.1em; text-transform: uppercase; color: #374151; margin: 0 0 16px; padding-bottom: 10px; border-bottom: 1px solid #f3f4f6; }
.tf-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(200px, 1fr)); gap: 14px; }
.tf-label { display: block; font-size: 12px; font-weight: 600; color: #374151; margin-bottom: 5px; }
.tf-req { color: #16a34a; }
.tf-input, .tf-select { width: 100%; padding: 10px 12px; border: 1px solid #d1d5db; border-radius: 8px; font-size: 14px; color: #111; background: #fff; box-sizing: border-box; font-family: inherit; }
.tf-input:focus, .tf-select:focus { outline: none; border-color: #16a34a; box-shadow: 0 0 0 2px rgba(22,163,74,0.12); }
.tf-tag { padding: 6px 14px; border-radius: 20px; border: 1px solid #d1d5db; background: #fff; color: #6b7280; font-size: 13px; font-weight: 500; cursor: pointer; }
.tf-tag.on { border-color: #16a34a; background: #f0fdf4; color: #166534; font-weight: 700; }
.tf-tags-row { display: flex; flex-wrap: wrap; gap: 8px; margin-bottom: 12px; }
.tf-footer { position: fixed; bottom: 0; left: 0; right: 0; z-index: 30; background: #fff; border-top: 1px solid #e5e7eb; padding: 14px 24px; display: flex; justify-content: flex-end; gap: 10px; box-shadow: 0 -4px 20px rgba(0,0,0,0.06); }
.tf-btn-cancel { padding: 11px 22px; border: 1px solid #d1d5db; border-radius: 8px; background: #fff; color: #374151; font-weight: 700; font-size: 14px; cursor: pointer; }
.tf-btn-save { padding: 11px 28px; border: none; border-radius: 8px; background: #16a34a; color: #fff; font-weight: 800; font-size: 14px; cursor: pointer; }
.tf-btn-save:disabled { opacity: 0.6; cursor: default; }
.tf-error { background: #fef2f2; border: 1px solid #fecaca; color: #dc2626; padding: 12px 16px; border-radius: 8px; margin-bottom: 16px; font-size: 13px; font-weight: 600; }
.tf-loading { text-align: center; padding: 80px 24px; color: #9ca3af; }
.tf-photo { width: 72px; height: 72px; border-radius: 50%; border: 2px dashed #d1d5db; display: flex; align-items: center; justify-content: center; cursor: pointer; overflow: hidden; flex-shrink: 0; }
.tf-photo img { width: 100%; height: 100%; object-fit: cover; }
`;

const RTR_DOCS = ['UK/Irish Passport', 'British National Overseas', 'EU Settled Status', 'Biometric Residence Permit', 'Share Code', 'Indefinite Leave to Remain', 'Other'];
const REL_OPTS = ['Spouse / Partner', 'Parent', 'Sibling', 'Friend', 'Employer', 'Other'];

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return <div className="tf-section"><h2 className="tf-section-title">{title}</h2>{children}</div>;
}

function Field({ label, required, children }: { label: string; required?: boolean; children: React.ReactNode }) {
  return <div><label className="tf-label">{label}{required && <span className="tf-req"> *</span>}</label>{children}</div>;
}

export default function TenantFormPage() {
  const { id } = useParams<{ id: string }>();
  const isEdit = !!id;
  const navigate = useNavigate();
  const location = useLocation();
  const { token } = useAuth();
  const saveInFlight = useRef(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const [form, setForm] = useState<TenantFormState>({ ...DEFAULT_TENANT_FORM });
  const [refForm, setRefForm] = useState<RefFormState>({ ...DEFAULT_REF_FORM });
  const [properties, setProperties] = useState<any[]>([]);
  const [propertyRooms, setPropertyRooms] = useState<any[]>([]);
  const [propertyMatches, setPropertyMatches] = useState<any[]>([]);
  const [matchesLoading, setMatchesLoading] = useState(false);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState('');
  const [existingImage, setExistingImage] = useState('');
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [savingRef, setSavingRef] = useState(false);
  const [error, setError] = useState('');

  const set = <K extends keyof TenantFormState>(key: K, val: TenantFormState[K]) =>
    setForm(p => ({ ...p, [key]: val }));

  const loadProperties = useCallback(async () => {
    if (!token) return;
    const r = await fetch(`${API_URL}/landlord/properties`, { headers: { Authorization: `Bearer ${token}` } });
    const d = await r.json();
    setProperties((d.properties || []).filter((p: any) => !p.archived));
  }, [token]);

  const loadRooms = useCallback(async (address: string) => {
    if (!address || !token) { setPropertyRooms([]); return; }
    try {
      const r = await fetch(`${API_URL}/landlord/rooms?property_address=${encodeURIComponent(address)}`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await r.json();
      setPropertyRooms(Array.isArray(data) ? data : []);
    } catch { setPropertyRooms([]); }
  }, [token]);

  const loadTenant = useCallback(async () => {
    if (!token || !id) return;
    setLoading(true);
    try {
      const r = await fetch(`${API_URL}/landlord/tenants`, { headers: { Authorization: `Bearer ${token}` } });
      const d = await r.json();
      const t = (d.tenants || []).find((x: any) => String(x.id) === id);
      if (!t) throw new Error('Tenant not found');
      setForm(tenantToForm(t));
      setExistingImage(t.image_url || '');
      if (t.property_address) loadRooms(t.property_address);
      const rr = await fetch(`${API_URL}/landlord/tenants/${id}/referencing`, { headers: { Authorization: `Bearer ${token}` } });
      if (rr.ok) {
        const rd = await rr.json();
        if (rd.referencing) {
          setRefForm({
            credit_check: rd.referencing.credit_check || 'pending',
            employment_check: rd.referencing.employment_check || 'pending',
            landlord_reference: rd.referencing.landlord_reference || 'pending',
            right_to_rent_verified: rd.referencing.right_to_rent_verified || 'pending',
            deposit_received: rd.referencing.deposit_received || 'pending',
            notes: rd.referencing.notes || '',
          });
        }
      }
      loadMatches(Number(id));
    } catch (e: any) {
      setError(e.message || 'Failed to load tenant');
    } finally { setLoading(false); }
  }, [token, id, loadRooms]);

  const loadMatches = async (tid: number) => {
    if (!token) return;
    setMatchesLoading(true);
    try {
      const r = await fetch(`${API_URL}/landlord/tenants/${tid}/property-matches`, { headers: { Authorization: `Bearer ${token}` } });
      if (r.ok) setPropertyMatches((await r.json()).matches || []);
    } catch { setPropertyMatches([]); }
    setMatchesLoading(false);
  };

  useEffect(() => { loadProperties(); }, [loadProperties]);
  useEffect(() => { if (isEdit) loadTenant(); }, [isEdit, loadTenant]);

  useEffect(() => {
    const ql = (location.state as { quickLet?: Partial<TenantFormState> })?.quickLet;
    if (!ql || isEdit) return;
    setForm(p => ({
      ...p,
      full_name: ql.full_name || p.full_name,
      email: ql.email || p.email,
      phone: ql.phone || p.phone,
      property_address: ql.property_address || p.property_address,
    }));
    if (ql.property_address) loadRooms(ql.property_address);
    window.history.replaceState({}, document.title);
  }, [location.state, isEdit, loadRooms]);

  const onPropertyChange = (addr: string) => {
    set('property_address', addr);
    set('room_number', '');
    loadRooms(addr);
  };

  const uploadImage = async (tenantId: number) => {
    if (!token || !imageFile) return true;
    const fd = new FormData();
    fd.append('image', imageFile);
    const r = await fetch(`${API_URL}/landlord/tenants/${tenantId}/image`, { method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd });
    return r.ok;
  };

  const save = async () => {
    if (saveInFlight.current || saving) return;
    if (!form.full_name.trim() || !form.email.trim() || !form.property_address) {
      setError('Full name, email and property are required');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    saveInFlight.current = true;
    setSaving(true); setError('');
    try {
      const url = isEdit ? `${API_URL}/landlord/tenants/${id}` : `${API_URL}/landlord/tenants`;
      const r = await fetch(url, {
        method: isEdit ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(serializeTenantForm(form, isEdit)),
      });
      const d = await r.json();
      if (!r.ok) {
        setError(d.error || 'Failed to save');
        saveInFlight.current = false; setSaving(false);
        return;
      }
      const tenantId = isEdit ? Number(id) : (d.tenant?.id ?? d.id);
      if (imageFile && tenantId) {
        const ok = await uploadImage(tenantId);
        if (!ok) showToast('Tenant saved but photo upload failed');
      }
      navigate('/landlord/tenants');
    } catch {
      setError('Failed to save tenant');
      saveInFlight.current = false; setSaving(false);
    }
  };

  const saveReferencing = async () => {
    if (!token || !id) return;
    setSavingRef(true);
    try {
      const r = await fetch(`${API_URL}/landlord/tenants/${id}/referencing`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ referencing: refForm }),
      });
      const d = await r.json();
      showToast(r.ok ? 'Referencing saved' : d.error || 'Failed');
    } catch { showToast('Network error'); }
    setSavingRef(false);
  };

  if (loading) {
    return <div className="tf-page"><style>{CSS}{PROPERTY_PAGE_CSS}</style><div className="tf-loading">Loading tenant…</div></div>;
  }

  const photoSrc = imagePreview || existingImage;

  return (
    <div className="tf-page">
      <style>{CSS}{PROPERTY_PAGE_CSS}</style>
      <header className="tf-top">
        <button type="button" className="tf-back" onClick={() => navigate('/landlord/tenants')}>← Back to Tenants</button>
        <h1 className="tf-title">{isEdit ? 'Edit Tenant' : 'Add Tenant'}</h1>
        <p className="tf-sub">{isEdit ? form.full_name || 'Update tenant details' : 'Add tenant details — lease is created separately in Leases'}</p>
      </header>

      <div className="tf-body">
        {error && <div className="tf-error">{error}</div>}

        <Section title="Basic Details">
          <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start' }}>
            <div>
              <div className="tf-photo" onClick={() => fileRef.current?.click()}>
                {photoSrc ? <img src={photoSrc} alt="" /> : <span style={{ fontSize: 11, color: '#9ca3af' }}>Photo</span>}
              </div>
              <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" style={{ display: 'none' }}
                onChange={e => { const f = e.target.files?.[0]; if (f) { setImageFile(f); const rd = new FileReader(); rd.onload = ev => setImagePreview(ev.target?.result as string); rd.readAsDataURL(f); } }} />
            </div>
            <div className="tf-grid" style={{ flex: 1 }}>
              <Field label="Full Name" required><input className="tf-input" value={form.full_name} onChange={e => set('full_name', e.target.value)} placeholder="Jane Smith" /></Field>
              <Field label="Email" required><input className="tf-input" type="email" value={form.email} onChange={e => set('email', e.target.value)} placeholder="jane@email.co.uk" /></Field>
              <Field label="Mobile"><input className="tf-input" value={form.phone} onChange={e => set('phone', e.target.value)} placeholder="07700 000000" /></Field>
              <Field label="Property" required>
                <select className="tf-select" value={form.property_address} onChange={e => onPropertyChange(e.target.value)}>
                  <option value="">— Select property —</option>
                  {properties.map((p: any) => <option key={p.id} value={p.address}>{p.address}, {p.postcode}</option>)}
                </select>
              </Field>
              <Field label="Move-in Date"><input type="date" className="tf-input" value={form.move_in_date} onChange={e => set('move_in_date', e.target.value)} /></Field>
              <Field label="Expected Move-out"><input type="date" className="tf-input" value={form.move_out_date} onChange={e => set('move_out_date', e.target.value)} /></Field>
              <Field label="Room / Unit">
                <select className="tf-select" value={form.room_number} onChange={e => set('room_number', e.target.value)} disabled={!form.property_address}>
                  <option value="">{propertyRooms.length ? '— Select room —' : 'No rooms / whole property'}</option>
                  {propertyRooms.map((r: any) => (
                    <option key={r.id} value={r.room_number || r.room_name}>{r.room_name}{r.room_number ? ` · ${r.room_number}` : ''}</option>
                  ))}
                </select>
              </Field>
              <div style={{ gridColumn: '1 / -1' }}>
                <Field label="Who is living here?"><input className="tf-input" value={form.occupants} onChange={e => set('occupants', e.target.value)} placeholder="e.g. couple, family of 4…" /></Field>
              </div>
            </div>
          </div>
        </Section>

        <Section title="Emergency Contact">
          <div className="tf-grid">
            <Field label="Contact Name"><input className="tf-input" value={form.emergency_contact_name} onChange={e => set('emergency_contact_name', e.target.value)} /></Field>
            <Field label="Contact Phone"><input className="tf-input" value={form.emergency_contact_phone} onChange={e => set('emergency_contact_phone', e.target.value)} /></Field>
            <Field label="Relationship">
              <select className="tf-select" value={form.emergency_contact_relationship} onChange={e => set('emergency_contact_relationship', e.target.value)}>
                <option value="">— Select —</option>
                {REL_OPTS.map(o => <option key={o} value={o}>{o}</option>)}
              </select>
            </Field>
          </div>
        </Section>

        <Section title="Guarantor (optional)">
          <div className="tf-grid">
            <Field label="Guarantor Name"><input className="tf-input" value={form.guarantor_name} onChange={e => set('guarantor_name', e.target.value)} /></Field>
            <Field label="Guarantor Phone"><input className="tf-input" value={form.guarantor_phone} onChange={e => set('guarantor_phone', e.target.value)} /></Field>
            <Field label="Guarantor Email"><input className="tf-input" type="email" value={form.guarantor_email} onChange={e => set('guarantor_email', e.target.value)} /></Field>
            <Field label="Relationship">
              <select className="tf-select" value={form.guarantor_relationship} onChange={e => set('guarantor_relationship', e.target.value)}>
                <option value="">— Select —</option>
                {REL_OPTS.map(o => <option key={o} value={o}>{o}</option>)}
              </select>
            </Field>
            <div style={{ gridColumn: '1 / -1' }}><Field label="Guarantor Address"><input className="tf-input" value={form.guarantor_address} onChange={e => set('guarantor_address', e.target.value)} /></Field></div>
          </div>
        </Section>

        <Section title="Right to Rent (UK law)">
          <div className="tf-grid">
            <Field label="Check Date"><input type="date" className="tf-input" value={form.right_to_rent_checked} onChange={e => set('right_to_rent_checked', e.target.value)} /></Field>
            <Field label="Document Type">
              <select className="tf-select" value={form.right_to_rent_doc_type} onChange={e => set('right_to_rent_doc_type', e.target.value)}>
                <option value="">— Select —</option>
                {RTR_DOCS.map(d => <option key={d} value={d}>{d}</option>)}
              </select>
            </Field>
            <Field label="Document Expiry"><input type="date" className="tf-input" value={form.right_to_rent_expiry} onChange={e => set('right_to_rent_expiry', e.target.value)} /></Field>
            <Field label="Share Code"><input className="tf-input" value={form.right_to_rent_share_code} onChange={e => set('right_to_rent_share_code', e.target.value)} placeholder="e.g. W2B3C4" /></Field>
          </div>
        </Section>

        {isEdit && (
          <>
            <Section title="Tenant Referencing">
              <div className="tf-grid">
                {(['credit_check', 'employment_check', 'landlord_reference', 'right_to_rent_verified', 'deposit_received'] as const).map(key => (
                  <Field key={key} label={key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}>
                    <select className="tf-select" value={refForm[key]} onChange={e => setRefForm(p => ({ ...p, [key]: e.target.value }))}>
                      <option value="pending">Pending</option><option value="passed">Passed</option><option value="failed">Failed</option><option value="waived">Waived</option>
                    </select>
                  </Field>
                ))}
                <div style={{ gridColumn: '1 / -1' }}><Field label="Notes"><input className="tf-input" value={refForm.notes} onChange={e => setRefForm(p => ({ ...p, notes: e.target.value }))} /></Field></div>
              </div>
              <button type="button" onClick={saveReferencing} disabled={savingRef} style={{ marginTop: 12, padding: '9px 18px', background: '#047857', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, cursor: 'pointer' }}>
                {savingRef ? 'Saving…' : 'Save Referencing'}
              </button>
            </Section>

            <Section title="Housing Preferences">
              <div className="tf-tags-row">
                {([
                  ['ground_floor_only', 'Ground floor only'], ['wheelchair_access', 'Wheelchair access'], ['lift_required', 'Lift required'],
                  ['no_stairs', 'No stairs'], ['parking_required', 'Parking'], ['near_transport', 'Near transport'],
                  ['near_hospital', 'Near hospital'], ['quiet_area', 'Quiet area'], ['pet_friendly', 'Pet friendly'], ['furnished_only', 'Furnished only'],
                ] as const).map(([key, label]) => {
                  const active = !!form.housing_requirements?.[key];
                  return (
                    <button key={key} type="button" className={`tf-tag${active ? ' on' : ''}`} onClick={() => setForm(p => ({
                      ...p, housing_requirements: { ...HOUSING_REQ_DEFAULTS, ...p.housing_requirements, [key]: !active },
                    }))}>{active ? '✓ ' : ''}{label}</button>
                  );
                })}
              </div>
              <div className="tf-grid">
                <Field label="Max Budget (£/mo)"><input type="number" className="tf-input" value={String(form.housing_requirements?.max_budget || '')} onChange={e => setForm(p => ({ ...p, housing_requirements: { ...HOUSING_REQ_DEFAULTS, ...p.housing_requirements, max_budget: e.target.value } }))} /></Field>
                <Field label="Preferred Areas"><input className="tf-input" value={form.housing_requirements?.preferred_areas || ''} onChange={e => setForm(p => ({ ...p, housing_requirements: { ...HOUSING_REQ_DEFAULTS, ...p.housing_requirements, preferred_areas: e.target.value } }))} placeholder="Southsea, PO4…" /></Field>
              </div>
              {propertyMatches.length > 0 && (
                <div style={{ marginTop: 14 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: '#374151', marginBottom: 8 }}>Suggested Properties {matchesLoading && '…'}</div>
                  {propertyMatches.slice(0, 5).map((m: any) => (
                    <div key={m.property_id} style={{ fontSize: 12, padding: '6px 0', color: m.match ? '#16a34a' : '#9ca3af' }}>
                      {m.match ? '✓' : '—'} {m.address} · £{Number(m.monthly_rent).toLocaleString()} ({m.score}%)
                    </div>
                  ))}
                </div>
              )}
            </Section>
          </>
        )}
      </div>

      <footer className="tf-footer">
        <button type="button" className="tf-btn-cancel" onClick={() => navigate('/landlord/tenants')}>← Back</button>
        <button type="button" className="tf-btn-save" onClick={save} disabled={saving}>{saving ? 'Saving…' : isEdit ? 'Save Changes' : 'Save Tenant'}</button>
      </footer>
    </div>
  );
}
